import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { defineStore } from 'pinia';
import { PostHog } from 'posthog-js';
import { Ref, ref } from 'vue';

import { debug } from '@silae/helpers';

type TrackingService<User = any> = {
	clearUserContext: () => void;
	initAppInsights: (appInsights: ApplicationInsights) => void;
	initPostHog: (postHog: PostHog | null) => void;
	setUserContext: (user: User) => void;
	setUserProperties: (userProperties: Record<string, any>) => void;
	trackEvent: (event: string, customProperties?: Record<string, any>) => void;
	trackPageView: () => void;
	hashEmail: (userEmail: string) => Promise<string>;
};

/***
 	You should know that AdBlocker manages mostly to prevent tracking calls, and it can create some errors.
 */
export const useTrackingService = defineStore<'tracking-service', TrackingService>('tracking-service', () => {
	const _appInsights: Ref<ApplicationInsights | undefined> = ref();
	const _postHog: Ref<PostHog | undefined> = ref();

	const initPostHog = (postHog: PostHog | null) => {
		if (postHog) {
			_postHog.value = postHog;
		}
	};

	const initAppInsights = (appInsight: ApplicationInsights) => (_appInsights.value = appInsight);

	const hashEmail = async (userEmail: string) => {
		//hash email
		const encoder = new TextEncoder();
		const data = encoder.encode(userEmail.toLowerCase());
		const hashBuffer = await crypto.subtle.digest('SHA-256', data);
		const hashArray = Array.from(new Uint8Array(hashBuffer));
		const hashedEmail = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
		return hashedEmail;
	};

	const setUserContext = async (userEmail: string) => {
		const hashedEmail = await hashEmail(userEmail);

		_postHog.value?.identify(hashedEmail, {
			email: hashedEmail
		});
	};

	const setUserProperties = (customProperties: Record<string, any>) => {
		_postHog.value?.setPersonProperties(customProperties);
	};

	const clearUserContext = () => {
		_appInsights.value?.clearAuthenticatedUserContext();
		_postHog.value?.reset();
	};

	const trackEvent = (event: string, props?: Record<string, any>) => {
		try {
			_postHog.value?.capture(event, props);
		} catch (err: unknown) {
			// add blockers can prevent tracking or create an error
			// TODO we should probably catch this using Sentry
			debug('tracking failed');
		}
	};

	const trackPageView = () => {
		try {
			_postHog.value?.capture('$pageview');
		} catch (err: unknown) {
			// add blocker can prevent tracking or create an error
			// TODO we should probably catch this using Sentry
			debug('tracking failed');
		}
	};

	return { clearUserContext, initAppInsights, initPostHog, setUserContext, setUserProperties, trackEvent, trackPageView, hashEmail };
});
